import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// ── Logique pure (testée dans pagination.test.js) ─────────────────────────────
// Calcul de la fenêtre de pagination (logique éprouvée type Mantine usePagination).
// Renvoie un tableau de numéros de page et de marqueurs DOTS ('…'), p.ex.
//   [1, '…', 29, 30, 31, '…', 58]   pour total=58, current=30.
// Garde toujours visibles : la page 1, la dernière, et `siblings` voisins autour de la
// page courante. Les ellipses ne sont posées que s'il y a au moins 2 pages masquées
// (sinon on affiche la page elle-même plutôt qu'un '…' qui cacherait une seule page).
export const DOTS = '…';

const range = (start, end) => {
  const length = end - start + 1;
  return length > 0 ? Array.from({ length }, (_, i) => start + i) : [];
};

export function paginationRange({ total, current, siblings = 1, boundaries = 1 }) {
  const totalPages = Math.max(total, 1);
  const page = Math.min(Math.max(current, 1), totalPages);

  // Nombre d'éléments affichés sans ellipse : voisins*2 + courante + 1ère + dernière + 2 ellipses.
  const totalPageNumbers = siblings * 2 + 3 + boundaries * 2;
  if (totalPageNumbers >= totalPages) {
    return range(1, totalPages);
  }

  const leftSiblingIndex = Math.max(page - siblings, boundaries + 1);
  const rightSiblingIndex = Math.min(page + siblings, totalPages - boundaries);

  // Ellipse seulement si elle masque ≥ 2 pages (gap d'exactement 1 → on montre la page).
  const showLeftDots = leftSiblingIndex > boundaries + 2;
  const showRightDots = rightSiblingIndex < totalPages - (boundaries + 1);

  if (!showLeftDots && showRightDots) {
    const leftItemCount = siblings * 2 + boundaries + 2;
    return [...range(1, leftItemCount), DOTS, ...range(totalPages - boundaries + 1, totalPages)];
  }

  if (showLeftDots && !showRightDots) {
    const rightItemCount = boundaries + 1 + siblings * 2;
    return [...range(1, boundaries), DOTS, ...range(totalPages - rightItemCount + 1, totalPages)];
  }

  return [
    ...range(1, boundaries),
    DOTS,
    ...range(leftSiblingIndex, rightSiblingIndex),
    DOTS,
    ...range(totalPages - boundaries + 1, totalPages),
  ];
}

// ── Composant ─────────────────────────────────────────────────────────────────
// Barre de pagination compacte et accessible : ‹ 1 … 29 [30] 31 … 58 ›.
// Remplace l'affichage de TOUS les numéros (jusqu'à 58 boutons sur le catalogue neuf).
// `onPageChange(n)` n'est appelé que pour une page valide et différente de l'actuelle.
export default function Pagination({ currentPage, totalPages, onPageChange, siblings = 1 }) {
  if (!totalPages || totalPages <= 1) return null;

  const items = paginationRange({ total: totalPages, current: currentPage, siblings });
  const go = (p) => {
    if (p >= 1 && p <= totalPages && p !== currentPage) onPageChange(p);
  };

  const base =
    'w-10 h-10 rounded-sm font-heading font-semibold text-sm transition-colors flex items-center justify-center active:scale-95';
  const arrow = `${base} bg-white border border-slate-200 text-slate-600 hover:border-secondary hover:text-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-600`;

  return (
    <nav
      className="flex items-center justify-center gap-1.5 sm:gap-2 mt-12 flex-wrap"
      data-testid="pagination"
      aria-label="Pagination du catalogue"
    >
      <button
        type="button"
        onClick={() => go(currentPage - 1)}
        disabled={currentPage <= 1}
        className={arrow}
        aria-label="Page précédente"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {items.map((it, i) =>
        it === DOTS ? (
          <span
            key={`dots-${i}`}
            className="w-10 h-10 flex items-center justify-center text-slate-400 font-heading select-none"
            aria-hidden="true"
          >
            {DOTS}
          </span>
        ) : (
          <button
            type="button"
            key={it}
            onClick={() => go(it)}
            aria-current={it === currentPage ? 'page' : undefined}
            className={`${base} ${
              it === currentPage
                ? 'bg-secondary text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-secondary hover:text-secondary'
            }`}
          >
            {it}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => go(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={arrow}
        aria-label="Page suivante"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </nav>
  );
}
